import type { Database } from 'sql.js';
import { createCorrelationId } from '@core/domain/utils/correlationId.ts';
import type {
  BeneficiarioOption,
  CicloOption,
  ComunaOption,
  EntregaEditRecord,
  EntregaExportRow,
  EntregaFormCatalog,
  EntregaListItem,
  EntregaParticipanteEditRecord,
  EntregaParticipanteListItem,
  EntregaRemisionEditRecord,
  EntregaRemisionListItem,
  ItemEntregaOption,
  NutricionistaOption,
  PendingEntregaParticipanteRow,
  PendingEntregaPush,
  PendingEntregaRemisionRow,
  ProductoEntregaOption,
  PuntoEntregaOption,
  SaveEntregaInput,
  SaveEntregaParticipanteInput,
  SaveEntregaRemisionInput,
} from '../../domain/models/EntregaCatalog.ts';
import type {
  EntregaCatalogRepositoryPort,
  EntregaChildrenRepositoryPort,
  EntregaRepositoryPort,
} from '../../domain/ports/EntregaPorts.ts';
import { computeRemanente } from '../../domain/entregaTotales.ts';
import {
  inClausePlaceholders,
  markRowFailed,
  markRowSyncWarning,
  markRowsSynced,
  normalizeSyncStatus,
  queryPublicIdNombreTable,
  readNumberOrNull,
  readInt,
  queryLocalBeneficiarios,
  readString,
  runSqlJsTransaction,
  type GetSqlJsDatabase,
} from '@src/modules/platform/local-sqlite/infrastructure/sqlJsRowHelpers.ts';
import { markLocalSqliteDirty } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';

export class SqlJsEntregaAdapter
  implements EntregaCatalogRepositoryPort, EntregaRepositoryPort, EntregaChildrenRepositoryPort
{
  constructor(private readonly getDb: GetSqlJsDatabase) {}

  async loadFormCatalog(): Promise<EntregaFormCatalog> {
    const core = await this.loadFormCatalogCore();
    const extras = await this.loadFormCatalogFormExtras();
    return { ...core, ...extras };
  }

  async loadFormCatalogCore(): Promise<
    Pick<EntregaFormCatalog, 'comunas' | 'items' | 'itemCiclosByItemPublicId' | 'comunaPublicIdsByItemPublicId'>
  > {
    const db = this.getDb();
    if (!db) {
      return { comunas: [], items: [], itemCiclosByItemPublicId: {}, comunaPublicIdsByItemPublicId: {} };
    }
    const items = this.queryItems(db);
    return {
      comunas: this.queryComunas(db),
      items,
      itemCiclosByItemPublicId: this.queryItemCiclosByItemPublicId(db),
      comunaPublicIdsByItemPublicId: this.queryComunaPublicIdsByItemPublicId(db, items),
    };
  }

  async loadFormCatalogFormExtras(): Promise<
    Pick<EntregaFormCatalog, 'nutricionistas' | 'ciclos' | 'tiposDocumento' | 'productosEntrega' | 'parentescos'>
  > {
    const db = this.getDb();
    if (!db) {
      return {
        nutricionistas: [],
        ciclos: [],
        tiposDocumento: [],
        productosEntrega: [],
        parentescos: [],
      };
    }
    return {
      nutricionistas: this.queryNutricionistas(db),
      ciclos: this.queryCiclos(db),
      tiposDocumento: queryPublicIdNombreTable(db, 'tipos_documento'),
      productosEntrega: this.queryProductosEntrega(db),
      parentescos: queryPublicIdNombreTable(db, 'parentescos'),
    };
  }

  async searchBeneficiariosByDocumento(query: string, itemPublicId = ''): Promise<BeneficiarioOption[]> {
    const db = this.getDb();
    if (!db) return [];
    return queryLocalBeneficiarios(db, query, itemPublicId, [
      'tipo_documento_public_id',
      'telefono1',
      'telefono2',
      'whatsapp',
    ]).map((row) => ({
      publicId: readString(row, 'public_id'),
      documento: readString(row, 'documento'),
      nombre: readString(row, 'nombre'),
      tipoDocumentoPublicId: readString(row, 'tipo_documento_public_id'),
      telefono1: readString(row, 'telefono1'),
      telefono2: readString(row, 'telefono2'),
      whatsapp: readString(row, 'whatsapp'),
    }));
  }

  async loadPuntosByComuna(comunaPublicId: string): Promise<PuntoEntregaOption[]> {
    const db = this.getDb();
    if (!db || !comunaPublicId.trim()) return [];
    return this.queryPuntos(db, comunaPublicId.trim());
  }

  async save(input: SaveEntregaInput): Promise<{ publicId: string }> {
    const db = this.getDb();
    if (!db) throw new Error('Base de datos local no disponible.');
    const publicId = createCorrelationId();
    runSqlJsTransaction(db, () => {
      db.run(
        `INSERT INTO entregas_paquetes (
          public_id, comuna_public_id, punto_entrega_public_id, direccion, fecha,
          item_public_id, ciclo_public_id, unidad_programa, punto_lat, punto_lng, gps_lat, gps_lng,
          sync_status, sync_error, fecha_creacion, fecha_actualizacion
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'pending', NULL, datetime('now'), datetime('now'))`,
        [
          publicId,
          input.comunaPublicId,
          input.puntoEntregaPublicId,
          input.direccion,
          input.fecha,
          input.itemPublicId,
          input.cicloPublicId,
          input.unidadPrograma,
          input.puntoLat,
          input.puntoLng,
          input.gpsLat,
          input.gpsLng,
        ],
      );
      for (const nutricionistaPublicId of input.nutricionistaPublicIds) {
        if (!nutricionistaPublicId.trim()) continue;
        db.run(
          `INSERT INTO entrega_nutricionista (entrega_public_id, nutricionista_public_id) VALUES (?, ?)`,
          [publicId, nutricionistaPublicId],
        );
      }
    });
    return { publicId };
  }

  async getForEdit(publicId: string): Promise<EntregaEditRecord | null> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return null;
    const stmt = db.prepare(
      `SELECT comuna_public_id, punto_entrega_public_id, direccion, fecha, item_public_id, ciclo_public_id, unidad_programa,
              punto_lat, punto_lng, gps_lat, gps_lng
       FROM entregas_paquetes WHERE public_id = ?`,
    );
    stmt.bind([id]);
    if (!stmt.step()) {
      stmt.free();
      return null;
    }
    const row = stmt.getAsObject() as Record<string, unknown>;
    stmt.free();
    const nutricionistas = this.nutricionistasByEntrega(db, [id]).get(id) ?? [];
    return {
      publicId: id,
      comunaPublicId: readString(row, 'comuna_public_id'),
      puntoEntregaPublicId: readString(row, 'punto_entrega_public_id'),
      direccion: readString(row, 'direccion'),
      fecha: readString(row, 'fecha'),
      itemPublicId: readString(row, 'item_public_id'),
      cicloPublicId: readString(row, 'ciclo_public_id'),
      unidadPrograma: readString(row, 'unidad_programa'),
      nutricionistaPublicIds: nutricionistas,
      puntoLat: readNumberOrNull(row, 'punto_lat'),
      puntoLng: readNumberOrNull(row, 'punto_lng'),
      gpsLat: readNumberOrNull(row, 'gps_lat'),
      gpsLng: readNumberOrNull(row, 'gps_lng'),
    };
  }

  async update(publicId: string, input: SaveEntregaInput): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return false;
    db.run('BEGIN');
    try {
      db.run(
        `UPDATE entregas_paquetes SET
          comuna_public_id = ?, punto_entrega_public_id = ?, direccion = ?, fecha = ?,
          item_public_id = ?, ciclo_public_id = ?, unidad_programa = ?, punto_lat = ?, punto_lng = ?,
          gps_lat = ?, gps_lng = ?, sync_status = 'pending', sync_error = NULL,
          fecha_actualizacion = datetime('now')
         WHERE public_id = ?`,
        [
          input.comunaPublicId,
          input.puntoEntregaPublicId,
          input.direccion,
          input.fecha,
          input.itemPublicId,
          input.cicloPublicId,
          input.unidadPrograma,
          input.puntoLat,
          input.puntoLng,
          input.gpsLat,
          input.gpsLng,
          id,
        ],
      );
      const changed = db.getRowsModified() > 0;
      if (!changed) {
        db.run('ROLLBACK');
        return false;
      }
      db.run(`DELETE FROM entrega_nutricionista WHERE entrega_public_id = ?`, [id]);
      for (const nutricionistaPublicId of input.nutricionistaPublicIds) {
        if (!nutricionistaPublicId.trim()) continue;
        db.run(
          `INSERT INTO entrega_nutricionista (entrega_public_id, nutricionista_public_id) VALUES (?, ?)`,
          [id, nutricionistaPublicId],
        );
      }
      db.run('COMMIT');
      markLocalSqliteDirty();
      return true;
    } catch (e) {
      db.run('ROLLBACK');
      throw e;
    }
  }

  async listEntregas(limit = 20): Promise<EntregaListItem[]> {
    const db = this.getDb();
    if (!db) return [];
    const max = Math.max(1, Math.min(limit, 100));
    const draft = this.queryListEntregaRows(db, { limit: max });
    const labels = this.nutricionistasLabelsByEntrega(db, draft.map((r) => r.publicId));
    return draft.map((row) => ({
      ...row,
      nutricionistasLabel: labels.get(row.publicId) ?? '—',
    }));
  }

  async listUnsynced(): Promise<EntregaListItem[]> {
    const db = this.getDb();
    if (!db) return [];
    const draft = this.queryListEntregaRows(db, { unsyncedOnly: true });
    const labels = this.nutricionistasLabelsByEntrega(db, draft.map((r) => r.publicId));
    return draft.map((row) => ({
      ...row,
      nutricionistasLabel: labels.get(row.publicId) ?? '—',
    }));
  }

  private queryListEntregaRows(
    db: Database,
    opts: { unsyncedOnly?: boolean; limit?: number },
  ): Array<Omit<EntregaListItem, 'nutricionistasLabel'> & { publicId: string }> {
    const where = opts.unsyncedOnly ? `WHERE e.sync_status != 'synced'` : '';
    const order = opts.unsyncedOnly ? 'ASC' : 'DESC';
    const limitClause = opts.limit != null ? ' LIMIT ?' : '';
    const stmt = db.prepare(
      `SELECT e.public_id, e.fecha, e.direccion, e.unidad_programa, e.sync_status, e.sync_error,
              e.punto_lat, e.punto_lng, e.gps_lat, e.gps_lng,
              c.nombre AS comuna_nombre, p.nombre AS punto_nombre, i.nombre AS item_nombre
       FROM entregas_paquetes e
       LEFT JOIN comunas c ON c.public_id = e.comuna_public_id
       LEFT JOIN puntos_entrega p ON p.public_id = e.punto_entrega_public_id
       LEFT JOIN items_entrega i ON i.public_id = e.item_public_id
       ${where}
       ORDER BY e.id ${order}${limitClause}`,
    );
    if (opts.limit != null) stmt.bind([opts.limit]);
    const draft: Array<Omit<EntregaListItem, 'nutricionistasLabel'> & { publicId: string }> = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      const syncStatus = readString(row, 'sync_status');
      draft.push({
        publicId,
        fecha: readString(row, 'fecha'),
        comunaNombre: readString(row, 'comuna_nombre') || '—',
        puntoNombre: readString(row, 'punto_nombre') || '—',
        direccion: readString(row, 'direccion'),
        itemNombre: readString(row, 'item_nombre') || '—',
        unidadPrograma: readString(row, 'unidad_programa'),
        puntoLat: readNumberOrNull(row, 'punto_lat'),
        puntoLng: readNumberOrNull(row, 'punto_lng'),
        gpsLat: readNumberOrNull(row, 'gps_lat'),
        gpsLng: readNumberOrNull(row, 'gps_lng'),
        syncStatus: normalizeSyncStatus(syncStatus),
        syncError: readString(row, 'sync_error') || null,
      });
    }
    stmt.free();
    return draft;
  }

  async listPendingPush(): Promise<PendingEntregaPush[]> {
    const rows = await this.loadPushRows(`WHERE sync_status IN ('pending', 'failed')`, 'ASC');
    return rows.map(({ syncStatus: _syncStatus, syncError: _syncError, ...row }) => row);
  }

  async listAllForExport(limit = 500): Promise<EntregaExportRow[]> {
    const max = Math.max(1, Math.min(limit, 500));
    return this.loadPushRows('', 'DESC', max);
  }

  private loadPushRows(
    whereClause: string,
    order: 'ASC' | 'DESC',
    limit?: number,
  ): EntregaExportRow[] {
    const db = this.getDb();
    if (!db) return [];
    const limitClause = limit != null ? ' LIMIT ?' : '';
    const stmt = db.prepare(
      `SELECT public_id, comuna_public_id, punto_entrega_public_id, direccion, fecha,
              item_public_id, ciclo_public_id, unidad_programa, punto_lat, punto_lng, gps_lat, gps_lng,
              sync_status, sync_error
       FROM entregas_paquetes ${whereClause} ORDER BY id ${order}${limitClause}`,
    );
    if (limit != null) stmt.bind([limit]);
    const draft: Array<
      Omit<EntregaExportRow, 'nutricionistaPublicIds' | 'participantes' | 'remisiones'> & { publicId: string }
    > = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      draft.push({
        publicId,
        comunaPublicId: readString(row, 'comuna_public_id'),
        puntoEntregaPublicId: readString(row, 'punto_entrega_public_id'),
        direccion: readString(row, 'direccion'),
        fecha: readString(row, 'fecha'),
        itemPublicId: readString(row, 'item_public_id'),
        cicloPublicId: readString(row, 'ciclo_public_id'),
        unidadPrograma: readString(row, 'unidad_programa'),
        puntoLat: readNumberOrNull(row, 'punto_lat'),
        puntoLng: readNumberOrNull(row, 'punto_lng'),
        gpsLat: readNumberOrNull(row, 'gps_lat'),
        gpsLng: readNumberOrNull(row, 'gps_lng'),
        syncStatus: normalizeSyncStatus(readString(row, 'sync_status')),
        syncError: readString(row, 'sync_error') || null,
      });
    }
    stmt.free();
    const rows: EntregaExportRow[] = [];
    const nutricionistas = this.nutricionistasByEntrega(db, draft.map((r) => r.publicId));
    for (const row of draft) {
      rows.push({
        ...row,
        nutricionistaPublicIds: nutricionistas.get(row.publicId) ?? [],
        participantes: this.pendingParticipantesByEntrega(db, row.publicId),
        remisiones: this.pendingRemisionesByEntrega(db, row.publicId),
      });
    }
    return rows;
  }

  async markSynced(publicIds: string[]): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowsSynced(db, 'entregas_paquetes', publicIds);
  }

  async markFailed(publicId: string, message: string): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowFailed(db, 'entregas_paquetes', publicId, message);
  }

  async setSyncWarning(publicId: string, message: string): Promise<void> {
    const db = this.getDb();
    if (!db) return;
    markRowSyncWarning(db, 'entregas_paquetes', publicId, message);
  }

  async clearSyncError(publicId: string): Promise<void> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return;
    this.touchEntregaPending(db, id);
    markLocalSqliteDirty();
  }

  async listParticipanteCicloIdsByEntrega(
    entregaPublicId: string,
  ): Promise<Array<{ publicId: string; cicloPublicId: string }>> {
    const db = this.getDb();
    const id = entregaPublicId.trim();
    if (!db || !id) return [];
    const stmt = db.prepare(
      `SELECT public_id, ciclo_public_id FROM entrega_participantes WHERE entrega_public_id = ? ORDER BY id ASC`,
    );
    stmt.bind([id]);
    const rows: Array<{ publicId: string; cicloPublicId: string }> = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      rows.push({
        publicId: readString(row, 'public_id'),
        cicloPublicId: readString(row, 'ciclo_public_id'),
      });
    }
    stmt.free();
    return rows;
  }

  async remapParticipanteCiclos(
    entregaPublicId: string,
    remaps: Array<{ publicId: string; cicloPublicId: string }>,
  ): Promise<void> {
    const db = this.getDb();
    const entregaId = entregaPublicId.trim();
    if (!db || !entregaId || remaps.length === 0) return;
    runSqlJsTransaction(db, () => {
      for (const row of remaps) {
        db.run(
          `UPDATE entrega_participantes SET ciclo_public_id = ?, fecha_actualizacion = datetime('now') WHERE public_id = ?`,
          [row.cicloPublicId, row.publicId],
        );
      }
      this.touchEntregaPending(db, entregaId);
    });
  }

  async listParticipantesByEntrega(entregaPublicId: string): Promise<EntregaParticipanteListItem[]> {
    const db = this.getDb();
    const id = entregaPublicId.trim();
    if (!db || !id) return [];
    const stmt = db.prepare(
      `SELECT p.public_id, p.beneficiario_documento, p.beneficiario_nombre, p.encuentro_educativo, p.entrega_bolsa,
              p.firma_media_id, p.foto_media_id,
              td.nombre AS tipo_documento_nombre, c.nombre AS ciclo_nombre
       FROM entrega_participantes p
       LEFT JOIN tipos_documento td ON td.public_id = p.tipo_documento_public_id
       LEFT JOIN ciclos c ON c.public_id = p.ciclo_public_id
       WHERE p.entrega_public_id = ?
       ORDER BY p.id ASC`,
    );
    stmt.bind([id]);
    const rows: EntregaParticipanteListItem[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      const productoPublicIds = this.productosByParticipante(db, publicId);
      rows.push({
        publicId,
        beneficiarioDocumento: readString(row, 'beneficiario_documento'),
        tipoDocumentoNombre: readString(row, 'tipo_documento_nombre') || '—',
        beneficiarioNombre: readString(row, 'beneficiario_nombre'),
        cicloNombre: readString(row, 'ciclo_nombre') || '—',
        productoPublicIds,
        productosLabel: this.productosLabel(db, productoPublicIds),
        encuentroEducativo: readString(row, 'encuentro_educativo') as 'si' | 'no' | 'no_aplica',
        entregaBolsa: readString(row, 'entrega_bolsa') as 'si' | 'no',
        firmaMediaId: readString(row, 'firma_media_id'),
        fotoMediaId: readString(row, 'foto_media_id'),
      });
    }
    stmt.free();
    return rows;
  }

  async getParticipanteForEdit(publicId: string): Promise<EntregaParticipanteEditRecord | null> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return null;
    const stmt = db.prepare(
      `SELECT public_id, entrega_public_id, ciclo_public_id, beneficiario_public_id, tipo_documento_public_id,
              beneficiario_documento, beneficiario_nombre, telefono1, telefono2, whatsapp,
              acudiente_autorizado, parentesco_public_id, nombre_acudiente, documento_acudiente,
              cedula_beneficiario_front_media_id, cedula_beneficiario_back_media_id,
              cedula_autorizado_front_media_id, cedula_autorizado_back_media_id, documento_autorizacion_media_id,
              encuentro_educativo, entrega_bolsa, firma_media_id, foto_media_id, observaciones
       FROM entrega_participantes WHERE public_id = ?`,
    );
    stmt.bind([id]);
    if (!stmt.step()) {
      stmt.free();
      return null;
    }
    const row = stmt.getAsObject() as Record<string, unknown>;
    stmt.free();
    return {
      publicId: id,
      entregaPublicId: readString(row, 'entrega_public_id'),
      cicloPublicId: readString(row, 'ciclo_public_id'),
      beneficiarioPublicId: readString(row, 'beneficiario_public_id'),
      tipoDocumentoPublicId: readString(row, 'tipo_documento_public_id'),
      beneficiarioDocumento: readString(row, 'beneficiario_documento'),
      beneficiarioNombre: readString(row, 'beneficiario_nombre'),
      telefono1: readString(row, 'telefono1'),
      telefono2: readString(row, 'telefono2'),
      whatsapp: readString(row, 'whatsapp'),
      productoPublicIds: this.productosByParticipante(db, id),
      acudienteAutorizado: readString(row, 'acudiente_autorizado') as 'si' | 'no',
      parentescoPublicId: readString(row, 'parentesco_public_id') || null,
      nombreAcudiente: readString(row, 'nombre_acudiente'),
      documentoAcudiente: readString(row, 'documento_acudiente'),
      cedulaBeneficiarioFrontMediaId: readString(row, 'cedula_beneficiario_front_media_id'),
      cedulaBeneficiarioBackMediaId: readString(row, 'cedula_beneficiario_back_media_id'),
      cedulaAutorizadoFrontMediaId: readString(row, 'cedula_autorizado_front_media_id'),
      cedulaAutorizadoBackMediaId: readString(row, 'cedula_autorizado_back_media_id'),
      documentoAutorizacionMediaId: readString(row, 'documento_autorizacion_media_id'),
      encuentroEducativo: readString(row, 'encuentro_educativo') as 'si' | 'no' | 'no_aplica',
      entregaBolsa: readString(row, 'entrega_bolsa') as 'si' | 'no',
      firmaMediaId: readString(row, 'firma_media_id'),
      fotoMediaId: readString(row, 'foto_media_id'),
      observaciones: readString(row, 'observaciones'),
    };
  }

  async hasParticipanteByBeneficiarioItemCiclo(
    beneficiarioPublicId: string,
    entregaPublicId: string,
    excludeParticipantePublicId?: string | null,
  ): Promise<boolean> {
    const db = this.getDb();
    const beneficiario = beneficiarioPublicId.trim();
    const entrega = entregaPublicId.trim();
    if (!db || !beneficiario || !entrega) return false;
    const exclude = excludeParticipantePublicId?.trim() ?? '';
    const stmt = exclude
      ? db.prepare(
          `SELECT 1
           FROM entrega_participantes p
           JOIN entregas_paquetes e ON e.public_id = p.entrega_public_id
           JOIN entregas_paquetes current ON current.public_id = ?
           WHERE p.beneficiario_public_id = ?
             AND e.item_public_id = current.item_public_id
             AND e.ciclo_public_id = current.ciclo_public_id
             AND p.public_id <> ?
           LIMIT 1`,
        )
      : db.prepare(
          `SELECT 1
           FROM entrega_participantes p
           JOIN entregas_paquetes e ON e.public_id = p.entrega_public_id
           JOIN entregas_paquetes current ON current.public_id = ?
           WHERE p.beneficiario_public_id = ?
             AND e.item_public_id = current.item_public_id
             AND e.ciclo_public_id = current.ciclo_public_id
           LIMIT 1`,
        );
    stmt.bind(exclude ? [entrega, beneficiario, exclude] : [entrega, beneficiario]);
    const found = stmt.step();
    stmt.free();
    return found;
  }

  async saveParticipante(input: SaveEntregaParticipanteInput): Promise<{ publicId: string }> {
    const db = this.getDb();
    if (!db) throw new Error('Base de datos local no disponible.');
    const publicId = createCorrelationId();
    runSqlJsTransaction(db, () => {
      db.run(
        `INSERT INTO entrega_participantes (
          public_id, entrega_public_id, ciclo_public_id, beneficiario_public_id, tipo_documento_public_id,
          beneficiario_documento, beneficiario_nombre, telefono1, telefono2, whatsapp,
          acudiente_autorizado, parentesco_public_id, nombre_acudiente, documento_acudiente,
          cedula_beneficiario_front_media_id, cedula_beneficiario_back_media_id,
          cedula_autorizado_front_media_id, cedula_autorizado_back_media_id, documento_autorizacion_media_id,
          encuentro_educativo, entrega_bolsa, firma_media_id, foto_media_id, observaciones,
          fecha_creacion, fecha_actualizacion
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [
          publicId,
          input.entregaPublicId,
          input.cicloPublicId,
          input.beneficiarioPublicId,
          input.tipoDocumentoPublicId,
          input.beneficiarioDocumento,
          input.beneficiarioNombre,
          input.telefono1,
          input.telefono2,
          input.whatsapp,
          input.acudienteAutorizado,
          input.parentescoPublicId,
          input.nombreAcudiente,
          input.documentoAcudiente,
          input.cedulaBeneficiarioFrontMediaId,
          input.cedulaBeneficiarioBackMediaId,
          input.cedulaAutorizadoFrontMediaId,
          input.cedulaAutorizadoBackMediaId,
          input.documentoAutorizacionMediaId,
          input.encuentroEducativo,
          input.entregaBolsa,
          input.firmaMediaId,
          input.fotoMediaId,
          input.observaciones,
        ],
      );
      this.replaceParticipanteProductos(db, publicId, input.productoPublicIds);
      this.touchEntregaPending(db, input.entregaPublicId);
    });
    return { publicId };
  }

  async updateParticipante(publicId: string, input: SaveEntregaParticipanteInput): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return false;
    db.run('BEGIN');
    try {
      db.run(
        `UPDATE entrega_participantes SET
          ciclo_public_id = ?, beneficiario_public_id = ?, tipo_documento_public_id = ?,
          beneficiario_documento = ?, beneficiario_nombre = ?, telefono1 = ?, telefono2 = ?, whatsapp = ?,
          acudiente_autorizado = ?, parentesco_public_id = ?, nombre_acudiente = ?, documento_acudiente = ?,
          cedula_beneficiario_front_media_id = ?, cedula_beneficiario_back_media_id = ?,
          cedula_autorizado_front_media_id = ?, cedula_autorizado_back_media_id = ?, documento_autorizacion_media_id = ?,
          encuentro_educativo = ?, entrega_bolsa = ?, firma_media_id = ?, foto_media_id = ?,
          observaciones = ?, fecha_actualizacion = datetime('now')
         WHERE public_id = ?`,
        [
          input.cicloPublicId,
          input.beneficiarioPublicId,
          input.tipoDocumentoPublicId,
          input.beneficiarioDocumento,
          input.beneficiarioNombre,
          input.telefono1,
          input.telefono2,
          input.whatsapp,
          input.acudienteAutorizado,
          input.parentescoPublicId,
          input.nombreAcudiente,
          input.documentoAcudiente,
          input.cedulaBeneficiarioFrontMediaId,
          input.cedulaBeneficiarioBackMediaId,
          input.cedulaAutorizadoFrontMediaId,
          input.cedulaAutorizadoBackMediaId,
          input.documentoAutorizacionMediaId,
          input.encuentroEducativo,
          input.entregaBolsa,
          input.firmaMediaId,
          input.fotoMediaId,
          input.observaciones,
          id,
        ],
      );
      if (db.getRowsModified() === 0) {
        db.run('ROLLBACK');
        return false;
      }
      this.replaceParticipanteProductos(db, id, input.productoPublicIds);
      this.touchEntregaPending(db, input.entregaPublicId);
      db.run('COMMIT');
      markLocalSqliteDirty();
      return true;
    } catch (e) {
      db.run('ROLLBACK');
      throw e;
    }
  }

  async patchParticipanteDeliveryMedia(
    publicId: string,
    entregaPublicId: string,
    patch: { firmaMediaId?: string; fotoMediaId?: string },
  ): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    const entregaId = entregaPublicId.trim();
    if (!db || !id || !entregaId) return false;
    const record = await this.getParticipanteForEdit(id);
    if (!record) return false;
    const firmaMediaId = patch.firmaMediaId?.trim() ?? record.firmaMediaId;
    const fotoMediaId = patch.fotoMediaId?.trim() ?? record.fotoMediaId;
    db.run(
      `UPDATE entrega_participantes SET
        firma_media_id = ?, foto_media_id = ?, fecha_actualizacion = datetime('now')
       WHERE public_id = ?`,
      [firmaMediaId, fotoMediaId, id],
    );
    if (db.getRowsModified() === 0) return false;
    this.touchEntregaPending(db, entregaId);
    markLocalSqliteDirty();
    return true;
  }

  async patchRemisionDeliveryMedia(
    publicId: string,
    entregaPublicId: string,
    patch: { firmaRecibeMediaId?: string; firmaEntregaMediaId?: string },
  ): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    const entregaId = entregaPublicId.trim();
    if (!db || !id || !entregaId) return false;
    const record = await this.getRemisionForEdit(id);
    if (!record) return false;
    const firmaRecibeMediaId = patch.firmaRecibeMediaId?.trim() ?? record.firmaRecibeMediaId;
    const firmaEntregaMediaId = patch.firmaEntregaMediaId?.trim() ?? record.firmaEntregaMediaId;
    db.run(
      `UPDATE entrega_remisiones SET
        firma_recibe_media_id = ?, firma_entrega_media_id = ?, fecha_actualizacion = datetime('now')
       WHERE public_id = ?`,
      [firmaRecibeMediaId, firmaEntregaMediaId, id],
    );
    if (db.getRowsModified() === 0) return false;
    this.touchEntregaPending(db, entregaId);
    markLocalSqliteDirty();
    return true;
  }

  async listRemisionesByEntrega(entregaPublicId: string): Promise<EntregaRemisionListItem[]> {
    const db = this.getDb();
    const id = entregaPublicId.trim();
    if (!db || !id) return [];
    const stmt = db.prepare(
      `SELECT public_id, nombre_despacha, vehiculo, fecha_hora, cantidad_solicitadas, cantidad_entregadas, nombre_recibe
       FROM entrega_remisiones WHERE entrega_public_id = ? ORDER BY id ASC`,
    );
    stmt.bind([id]);
    const rows: EntregaRemisionListItem[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const solicitadas = readInt(row, 'cantidad_solicitadas');
      const entregadas = readInt(row, 'cantidad_entregadas');
      rows.push({
        publicId: readString(row, 'public_id'),
        nombreDespacha: readString(row, 'nombre_despacha'),
        vehiculo: readString(row, 'vehiculo'),
        fechaHora: readString(row, 'fecha_hora'),
        cantidadSolicitadas: solicitadas,
        cantidadEntregadas: entregadas,
        remanente: computeRemanente(solicitadas, entregadas),
        nombreRecibe: readString(row, 'nombre_recibe'),
      });
    }
    stmt.free();
    return rows;
  }

  async getRemisionForEdit(publicId: string): Promise<EntregaRemisionEditRecord | null> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return null;
    const stmt = db.prepare(
      `SELECT public_id, entrega_public_id, nombre_despacha, vehiculo, fecha_hora,
              cantidad_solicitadas, cantidad_entregadas, cantidad_bolsas_solicitadas, cantidad_bolsas_entregadas,
              observaciones, nombre_recibe, cedula_recibe, firma_recibe_media_id,
              nombre_entrega, cedula_entrega, firma_entrega_media_id
       FROM entrega_remisiones WHERE public_id = ?`,
    );
    stmt.bind([id]);
    if (!stmt.step()) {
      stmt.free();
      return null;
    }
    const row = stmt.getAsObject() as Record<string, unknown>;
    stmt.free();
    return {
      publicId: id,
      entregaPublicId: readString(row, 'entrega_public_id'),
      nombreDespacha: readString(row, 'nombre_despacha'),
      vehiculo: readString(row, 'vehiculo'),
      fechaHora: readString(row, 'fecha_hora'),
      cantidadSolicitadas: readInt(row, 'cantidad_solicitadas'),
      cantidadEntregadas: readInt(row, 'cantidad_entregadas'),
      cantidadBolsasSolicitadas: readInt(row, 'cantidad_bolsas_solicitadas'),
      cantidadBolsasEntregadas: readInt(row, 'cantidad_bolsas_entregadas'),
      observaciones: readString(row, 'observaciones'),
      nombreRecibe: readString(row, 'nombre_recibe'),
      cedulaRecibe: readString(row, 'cedula_recibe'),
      firmaRecibeMediaId: readString(row, 'firma_recibe_media_id'),
      nombreEntrega: readString(row, 'nombre_entrega'),
      cedulaEntrega: readString(row, 'cedula_entrega'),
      firmaEntregaMediaId: readString(row, 'firma_entrega_media_id'),
    };
  }

  async saveRemision(input: SaveEntregaRemisionInput): Promise<{ publicId: string }> {
    const db = this.getDb();
    if (!db) throw new Error('Base de datos local no disponible.');
    const publicId = createCorrelationId();
    const remanente = computeRemanente(input.cantidadSolicitadas, input.cantidadEntregadas);
    const remanenteBolsas = computeRemanente(input.cantidadBolsasSolicitadas, input.cantidadBolsasEntregadas);
    runSqlJsTransaction(db, () => {
      db.run(
        `INSERT INTO entrega_remisiones (
          public_id, entrega_public_id, nombre_despacha, vehiculo, fecha_hora,
          cantidad_solicitadas, cantidad_entregadas, cantidad_bolsas_solicitadas, cantidad_bolsas_entregadas,
          remanente, remanente_bolsas, observaciones, nombre_recibe, cedula_recibe, firma_recibe_media_id,
          nombre_entrega, cedula_entrega, firma_entrega_media_id, foto_media_id,
          fecha_creacion, fecha_actualizacion
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
        [
          publicId,
          input.entregaPublicId,
          input.nombreDespacha,
          input.vehiculo,
          input.fechaHora,
          input.cantidadSolicitadas,
          input.cantidadEntregadas,
          input.cantidadBolsasSolicitadas,
          input.cantidadBolsasEntregadas,
          remanente,
          remanenteBolsas,
          input.observaciones,
          input.nombreRecibe,
          input.cedulaRecibe,
          input.firmaRecibeMediaId,
          input.nombreEntrega,
          input.cedulaEntrega,
          input.firmaEntregaMediaId,
          '',
        ],
      );
      this.touchEntregaPending(db, input.entregaPublicId);
    });
    return { publicId };
  }

  async updateRemision(publicId: string, input: SaveEntregaRemisionInput): Promise<boolean> {
    const db = this.getDb();
    const id = publicId.trim();
    if (!db || !id) return false;
    const remanente = computeRemanente(input.cantidadSolicitadas, input.cantidadEntregadas);
    const remanenteBolsas = computeRemanente(input.cantidadBolsasSolicitadas, input.cantidadBolsasEntregadas);
    db.run(
      `UPDATE entrega_remisiones SET
        nombre_despacha = ?, vehiculo = ?, fecha_hora = ?,
        cantidad_solicitadas = ?, cantidad_entregadas = ?,
        cantidad_bolsas_solicitadas = ?, cantidad_bolsas_entregadas = ?,
        remanente = ?, remanente_bolsas = ?, observaciones = ?,
        nombre_recibe = ?, cedula_recibe = ?, firma_recibe_media_id = ?,
        nombre_entrega = ?, cedula_entrega = ?, firma_entrega_media_id = ?,
        fecha_actualizacion = datetime('now')
       WHERE public_id = ?`,
      [
        input.nombreDespacha,
        input.vehiculo,
        input.fechaHora,
        input.cantidadSolicitadas,
        input.cantidadEntregadas,
        input.cantidadBolsasSolicitadas,
        input.cantidadBolsasEntregadas,
        remanente,
        remanenteBolsas,
        input.observaciones,
        input.nombreRecibe,
        input.cedulaRecibe,
        input.firmaRecibeMediaId,
        input.nombreEntrega,
        input.cedulaEntrega,
        input.firmaEntregaMediaId,
        id,
      ],
    );
    if (db.getRowsModified() === 0) return false;
    this.touchEntregaPending(db, input.entregaPublicId);
    markLocalSqliteDirty();
    return true;
  }

  private nutricionistasLabelsByEntrega(db: Database, entregaPublicIds: string[]): Map<string, string> {
    const map = new Map<string, string>();
    if (entregaPublicIds.length === 0) return map;
    const stmt = db.prepare(
      `SELECT en.entrega_public_id, group_concat(n.nombre, ', ') AS labels
       FROM entrega_nutricionista en
       JOIN nutricionistas n ON n.public_id = en.nutricionista_public_id
       WHERE en.entrega_public_id IN (${inClausePlaceholders(entregaPublicIds.length)})
       GROUP BY en.entrega_public_id`,
    );
    stmt.bind(entregaPublicIds);
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const id = readString(row, 'entrega_public_id');
      const labels = readString(row, 'labels');
      if (id) map.set(id, labels || '—');
    }
    stmt.free();
    return map;
  }

  private nutricionistasByEntrega(db: Database, entregaPublicIds: string[]): Map<string, string[]> {
    const map = new Map<string, string[]>();
    if (entregaPublicIds.length === 0) return map;
    const stmt = db.prepare(
      `SELECT entrega_public_id, nutricionista_public_id
       FROM entrega_nutricionista
       WHERE entrega_public_id IN (${inClausePlaceholders(entregaPublicIds.length)})`,
    );
    stmt.bind(entregaPublicIds);
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const entregaId = readString(row, 'entrega_public_id');
      const nutricionistaId = readString(row, 'nutricionista_public_id');
      if (!entregaId || !nutricionistaId) continue;
      const prev = map.get(entregaId) ?? [];
      prev.push(nutricionistaId);
      map.set(entregaId, prev);
    }
    stmt.free();
    return map;
  }

  private queryComunas(db: Database): ComunaOption[] {
    return queryPublicIdNombreTable(db, 'comunas');
  }

  private queryComunaPublicIdsByItemPublicId(
    db: Database,
    items: ItemEntregaOption[],
  ): Record<string, string[]> {
    const byItem = Object.fromEntries(items.map((item) => [item.publicId, [] as string[]])) as Record<string, string[]>;
    const unrestrictedComunas: string[] = [];
    const unrestrictedStmt = db.prepare(
      `SELECT DISTINCT pe.comuna_public_id
       FROM puntos_entrega pe
       WHERE pe.estado = 1
         AND NOT EXISTS (
           SELECT 1 FROM punto_entrega_item pei WHERE pei.punto_entrega_public_id = pe.public_id
         )`,
    );
    while (unrestrictedStmt.step()) {
      const comunaPublicId = readString(unrestrictedStmt.getAsObject() as Record<string, unknown>, 'comuna_public_id');
      if (comunaPublicId) unrestrictedComunas.push(comunaPublicId);
    }
    unrestrictedStmt.free();
    for (const item of items) {
      byItem[item.publicId] = [...unrestrictedComunas];
    }
    const linkedStmt = db.prepare(
      `SELECT DISTINCT pe.comuna_public_id, pei.item_entrega_public_id
       FROM puntos_entrega pe
       INNER JOIN punto_entrega_item pei ON pei.punto_entrega_public_id = pe.public_id
       WHERE pe.estado = 1`,
    );
    while (linkedStmt.step()) {
      const row = linkedStmt.getAsObject() as Record<string, unknown>;
      const comunaPublicId = readString(row, 'comuna_public_id');
      const itemPublicId = readString(row, 'item_entrega_public_id');
      if (!comunaPublicId || !itemPublicId || !byItem[itemPublicId]) continue;
      if (!byItem[itemPublicId]!.includes(comunaPublicId)) byItem[itemPublicId]!.push(comunaPublicId);
    }
    linkedStmt.free();
    return byItem;
  }

  private queryPuntos(db: Database, comunaPublicId: string): PuntoEntregaOption[] {
    const stmt = db.prepare(
      `SELECT pe.public_id, pe.comuna_public_id, pe.nombre, pe.direccion, pe.lat, pe.lng,
              pei.item_entrega_public_id
       FROM puntos_entrega pe
       LEFT JOIN punto_entrega_item pei ON pei.punto_entrega_public_id = pe.public_id
       WHERE pe.estado = 1 AND pe.comuna_public_id = ?
       ORDER BY pe.nombre COLLATE NOCASE, pei.item_entrega_public_id`,
    );
    stmt.bind([comunaPublicId]);
    const byPunto = new Map<string, PuntoEntregaOption>();
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      if (!publicId) continue;
      let punto = byPunto.get(publicId);
      if (!punto) {
        punto = {
          publicId,
          comunaPublicId: readString(row, 'comuna_public_id'),
          nombre: readString(row, 'nombre'),
          direccion: readString(row, 'direccion'),
          lat: readNumberOrNull(row, 'lat'),
          lng: readNumberOrNull(row, 'lng'),
          itemsEntregaPublicIds: [],
        };
        byPunto.set(publicId, punto);
      }
      const itemPublicId = readString(row, 'item_entrega_public_id');
      if (itemPublicId) punto.itemsEntregaPublicIds.push(itemPublicId);
    }
    stmt.free();
    return [...byPunto.values()];
  }

  private queryItems(db: Database): ItemEntregaOption[] {
    const stmt = db.prepare(
      `SELECT public_id, nombre, unidad_programa FROM items_entrega WHERE estado = 1 ORDER BY nombre COLLATE NOCASE`,
    );
    const rows: ItemEntregaOption[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      rows.push({
        publicId: readString(row, 'public_id'),
        nombre: readString(row, 'nombre'),
        unidadPrograma: readString(row, 'unidad_programa'),
      });
    }
    stmt.free();
    return rows;
  }

  private queryNutricionistas(db: Database): NutricionistaOption[] {
    return this.queryCatalogWithItemLinks(db, 'nutricionistas', 'nutricionista_item', 'nutricionista_public_id');
  }

  private queryProductosEntrega(db: Database): ProductoEntregaOption[] {
    const stmt = db.prepare(
      `SELECT pe.public_id, pe.nombre, pe.codigo, pei.item_entrega_public_id
       FROM productos_entrega pe
       LEFT JOIN producto_entrega_item pei ON pei.producto_entrega_public_id = pe.public_id
       WHERE pe.estado = 1
       ORDER BY pe.nombre COLLATE NOCASE, pei.item_entrega_public_id`,
    );
    const byId = new Map<string, ProductoEntregaOption>();
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      if (!publicId) continue;
      let producto = byId.get(publicId);
      if (!producto) {
        producto = {
          publicId,
          nombre: readString(row, 'nombre'),
          codigo: readString(row, 'codigo'),
          itemsPublicIds: [],
        };
        byId.set(publicId, producto);
      }
      const itemId = readString(row, 'item_entrega_public_id');
      if (itemId) producto.itemsPublicIds.push(itemId);
    }
    stmt.free();
    return [...byId.values()];
  }

  private queryCatalogWithItemLinks(
    db: Database,
    entityTable: string,
    junctionTable: string,
    entityCol: string,
  ): NutricionistaOption[] {
    const stmt = db.prepare(
      `SELECT e.public_id, e.nombre, j.item_entrega_public_id
       FROM ${entityTable} e
       LEFT JOIN ${junctionTable} j ON j.${entityCol} = e.public_id
       WHERE e.estado = 1
       ORDER BY e.nombre COLLATE NOCASE, j.item_entrega_public_id`,
    );
    const byId = new Map<string, NutricionistaOption>();
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      if (!publicId) continue;
      let entity = byId.get(publicId);
      if (!entity) {
        entity = { publicId, nombre: readString(row, 'nombre'), itemsPublicIds: [] };
        byId.set(publicId, entity);
      }
      const itemId = readString(row, 'item_entrega_public_id');
      if (itemId) entity.itemsPublicIds.push(itemId);
    }
    stmt.free();
    return [...byId.values()];
  }

  private queryItemCiclosByItemPublicId(db: Database): Record<string, string[]> {
    const stmt = db.prepare(
      `SELECT item_entrega_public_id, ciclo_public_id FROM item_entrega_ciclo ORDER BY item_entrega_public_id, ciclo_public_id`,
    );
    const out: Record<string, string[]> = {};
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const itemId = readString(row, 'item_entrega_public_id');
      const cicloId = readString(row, 'ciclo_public_id');
      if (!itemId || !cicloId) continue;
      (out[itemId] ??= []).push(cicloId);
    }
    stmt.free();
    return out;
  }

  private queryCiclos(db: Database): CicloOption[] {
    const stmt = db.prepare(
      `SELECT public_id, nombre, numero, fecha_inicio, fecha_fin FROM ciclos WHERE estado = 1 ORDER BY numero ASC, nombre COLLATE NOCASE`,
    );
    const rows: CicloOption[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      rows.push({
        publicId: readString(row, 'public_id'),
        nombre: readString(row, 'nombre'),
        numero: readInt(row, 'numero'),
        fechaInicio: readString(row, 'fecha_inicio'),
        fechaFin: readString(row, 'fecha_fin'),
      });
    }
    stmt.free();
    return rows;
  }

  private touchEntregaPending(db: Database, entregaPublicId: string): void {
    if (!entregaPublicId.trim()) return;
    db.run(
      `UPDATE entregas_paquetes SET sync_status = 'pending', sync_error = NULL, fecha_actualizacion = datetime('now') WHERE public_id = ?`,
      [entregaPublicId.trim()],
    );
  }

  private replaceParticipanteProductos(db: Database, participantePublicId: string, productoPublicIds: string[]): void {
    db.run(`DELETE FROM entrega_participante_producto WHERE participante_public_id = ?`, [participantePublicId]);
    for (const productoPublicId of productoPublicIds) {
      if (!productoPublicId.trim()) continue;
      db.run(
        `INSERT INTO entrega_participante_producto (participante_public_id, producto_public_id) VALUES (?, ?)`,
        [participantePublicId, productoPublicId],
      );
    }
  }

  private productosByParticipante(db: Database, participantePublicId: string): string[] {
    const stmt = db.prepare(
      `SELECT producto_public_id FROM entrega_participante_producto WHERE participante_public_id = ?`,
    );
    stmt.bind([participantePublicId]);
    const ids: string[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const id = readString(row, 'producto_public_id');
      if (id) ids.push(id);
    }
    stmt.free();
    return ids;
  }

  private productosLabel(db: Database, productoPublicIds: string[]): string {
    if (productoPublicIds.length === 0) return '—';
    const stmt = db.prepare(
      `SELECT group_concat(nombre, ', ') AS labels FROM productos_entrega
       WHERE public_id IN (${inClausePlaceholders(productoPublicIds.length)})`,
    );
    stmt.bind(productoPublicIds);
    let label = '—';
    if (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      label = readString(row, 'labels') || '—';
    }
    stmt.free();
    return label;
  }

  private pendingParticipantesByEntrega(db: Database, entregaPublicId: string): PendingEntregaParticipanteRow[] {
    const stmt = db.prepare(
      `SELECT public_id, ciclo_public_id, beneficiario_public_id, tipo_documento_public_id,
              beneficiario_documento, beneficiario_nombre, telefono1, telefono2, whatsapp,
              acudiente_autorizado, parentesco_public_id, nombre_acudiente, documento_acudiente,
              cedula_beneficiario_front_media_id, cedula_beneficiario_back_media_id,
              cedula_autorizado_front_media_id, cedula_autorizado_back_media_id, documento_autorizacion_media_id,
              encuentro_educativo, entrega_bolsa, firma_media_id, foto_media_id, observaciones
       FROM entrega_participantes WHERE entrega_public_id = ? ORDER BY id ASC`,
    );
    stmt.bind([entregaPublicId]);
    const rows: PendingEntregaParticipanteRow[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      const publicId = readString(row, 'public_id');
      rows.push({
        publicId,
        cicloPublicId: readString(row, 'ciclo_public_id'),
        beneficiarioPublicId: readString(row, 'beneficiario_public_id'),
        tipoDocumentoPublicId: readString(row, 'tipo_documento_public_id'),
        beneficiarioDocumento: readString(row, 'beneficiario_documento'),
        beneficiarioNombre: readString(row, 'beneficiario_nombre'),
        telefono1: readString(row, 'telefono1'),
        telefono2: readString(row, 'telefono2'),
        whatsapp: readString(row, 'whatsapp'),
        productoPublicIds: this.productosByParticipante(db, publicId),
        acudienteAutorizado: readString(row, 'acudiente_autorizado') as 'si' | 'no',
        parentescoPublicId: readString(row, 'parentesco_public_id') || null,
        nombreAcudiente: readString(row, 'nombre_acudiente'),
        documentoAcudiente: readString(row, 'documento_acudiente'),
        cedulaBeneficiarioFrontMediaId: readString(row, 'cedula_beneficiario_front_media_id'),
        cedulaBeneficiarioBackMediaId: readString(row, 'cedula_beneficiario_back_media_id'),
        cedulaAutorizadoFrontMediaId: readString(row, 'cedula_autorizado_front_media_id'),
        cedulaAutorizadoBackMediaId: readString(row, 'cedula_autorizado_back_media_id'),
        documentoAutorizacionMediaId: readString(row, 'documento_autorizacion_media_id'),
        encuentroEducativo: readString(row, 'encuentro_educativo') as 'si' | 'no' | 'no_aplica',
        entregaBolsa: readString(row, 'entrega_bolsa') as 'si' | 'no',
        observaciones: readString(row, 'observaciones'),
        firmaMediaId: readString(row, 'firma_media_id'),
        fotoMediaId: readString(row, 'foto_media_id'),
      });
    }
    stmt.free();
    return rows;
  }

  private pendingRemisionesByEntrega(db: Database, entregaPublicId: string): PendingEntregaRemisionRow[] {
    const stmt = db.prepare(
      `SELECT public_id, nombre_despacha, vehiculo, fecha_hora, cantidad_solicitadas, cantidad_entregadas,
              cantidad_bolsas_solicitadas, cantidad_bolsas_entregadas,
              observaciones, nombre_recibe, cedula_recibe, firma_recibe_media_id,
              nombre_entrega, cedula_entrega, firma_entrega_media_id
       FROM entrega_remisiones WHERE entrega_public_id = ? ORDER BY id ASC`,
    );
    stmt.bind([entregaPublicId]);
    const rows: PendingEntregaRemisionRow[] = [];
    while (stmt.step()) {
      const row = stmt.getAsObject() as Record<string, unknown>;
      rows.push({
        publicId: readString(row, 'public_id'),
        nombreDespacha: readString(row, 'nombre_despacha'),
        vehiculo: readString(row, 'vehiculo'),
        fechaHora: readString(row, 'fecha_hora'),
        cantidadSolicitadas: readInt(row, 'cantidad_solicitadas'),
        cantidadEntregadas: readInt(row, 'cantidad_entregadas'),
        cantidadBolsasSolicitadas: readInt(row, 'cantidad_bolsas_solicitadas'),
        cantidadBolsasEntregadas: readInt(row, 'cantidad_bolsas_entregadas'),
        observaciones: readString(row, 'observaciones'),
        nombreRecibe: readString(row, 'nombre_recibe'),
        cedulaRecibe: readString(row, 'cedula_recibe'),
        nombreEntrega: readString(row, 'nombre_entrega'),
        cedulaEntrega: readString(row, 'cedula_entrega'),
        firmaRecibeMediaId: readString(row, 'firma_recibe_media_id'),
        firmaEntregaMediaId: readString(row, 'firma_entrega_media_id'),
      });
    }
    stmt.free();
    return rows;
  }
}
